import { Button, Icon, Shimmer, Text } from '@pukka/ui-components';

import { BillingPriceSummaryDetail } from './BillingPriceSummaryDetail';
import { BillingPriceSummaryDiscount } from './BillingPriceSummaryDiscount';
import { IPaymentState } from './BillingReceipt.state';

interface BillingPriceSummaryProps {
  readonly isLoading?: boolean;
  readonly disabled?: boolean;
  readonly coupon: IPaymentState['amount']['coupon'];
  readonly total: IPaymentState['amount']['total'];
  readonly onRemoveCoupon: IPaymentState['onRemoveCoupon'];
}

function BillingPriceSummary({ disabled, isLoading, coupon, total, onRemoveCoupon }: BillingPriceSummaryProps) {
  const { newExpiry, finalPrice, originalPrice, description } = total;

  return (
    <div className="border-t border-t-neutral-200 view-transition-[price-summary]" data-testid="billing-price-summary">
      {coupon.amount || coupon.monthExtension ? (
        <>
          <BillingPriceSummaryDetail loading={isLoading} label="Sub Total" price={finalPrice}>
            {finalPrice !== originalPrice ? (
              <BillingPriceSummaryDiscount loading={isLoading} price={originalPrice} description={description} />
            ) : null}
          </BillingPriceSummaryDetail>
          <BillingPriceSummaryDetail
            loading={isLoading}
            label={
              <Text as="span" className="flex flex-row items-center">
                <Text as="span" className="inline-block">
                  Coupon Discount
                </Text>
                <Button
                  disabled={disabled || isLoading}
                  variant="nude"
                  size="none"
                  className="ml-1.5 rounded-md p-0.5"
                  data-testid="remove-coupon-btn"
                  onClick={onRemoveCoupon}
                >
                  <Icon icon="wrong" size="sm" color={disabled || isLoading ? 'neutral' : 'red'} />
                </Button>
              </Text>
            }
            price={-coupon.amount}
          >
            <BillingPriceSummaryDiscount loading={isLoading} description={coupon.description} />
          </BillingPriceSummaryDetail>
        </>
      ) : null}
      <BillingPriceSummaryDetail loading={isLoading} bigLabel label="Total" price={finalPrice - coupon.amount}>
        <div className="flex flex-row justify-between">
          {/* eslint-disable-next-line no-nested-ternary */}
          {newExpiry ? (
            isLoading ? (
              <Shimmer size="md" />
            ) : (
              <Text variant="body-2" className="flex-1 text-neutral-600 md:whitespace-pre">
                expiration date will be extended to
                {'\n'}
                <Text as="span" color="blue">
                  {newExpiry}
                </Text>
              </Text>
            )
          ) : (
            <div />
          )}
          {finalPrice !== originalPrice && !coupon.amount ? (
            <BillingPriceSummaryDiscount loading={isLoading} price={originalPrice} description={description} />
          ) : null}
        </div>
      </BillingPriceSummaryDetail>
    </div>
  );
}

export { BillingPriceSummary };
